import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

class SearchPageInputComponent extends Component {
  requiredRefs = [
    'searchPageInput',
    'predictiveDropdown',
    'predictiveList',
  ];

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('click', this.#handleDocumentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('click', this.#handleDocumentClick);
  }

  handleSearch = debounce(async () => {
    const searchTerm = this.refs.searchPageInput.value.trim();

    if (!searchTerm) {
      this.#closeDropdown();
      return;
    }

    await this.#fetchResults(searchTerm);
  }, 150);

  async #fetchResults(searchTerm) {
    const list = this.refs.predictiveList;

    list.innerHTML = `
      <div class="search-predictive-loading">
        Searching...
      </div>
    `;

    this.#openDropdown();

    try {
      const url = new URL(
        `${window.Shopify.routes.root}search/suggest.json`,
        window.location.origin
      );

      url.searchParams.set('q', searchTerm);

      // Search across all of the main Shopify resource types.
      url.searchParams.set(
        'resources[type]',
        'product,collection,page,article'
      );

      // Four results TOTAL, rather than four of each type.
      url.searchParams.set('resources[limit]', '4');
      url.searchParams.set('resources[limit_scope]', 'all');

      // Keep unavailable products out of the suggestions.
      url.searchParams.set(
        'resources[options][unavailable_products]',
        'hide'
      );

      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error(`Predictive search failed: ${response.status}`);
      }

      const data = await response.json();

      const results = this.#combineResults(data);

      list.innerHTML = '';

      if (results.length) {
        results.forEach((result) => {
          this.#renderResult(result);
        });
      } else {
        this.#renderEmptyState(searchTerm);
      }

      this.#renderMoreResults(searchTerm);

      this.#openDropdown();
    } catch (error) {
      console.error('Predictive search error:', error);

      list.innerHTML = '';

      this.#renderEmptyState(searchTerm);
      this.#renderMoreResults(searchTerm);

      this.#openDropdown();
    }
  }

  #combineResults(data) {
    const results = data?.resources?.results || {};

    const products = results.products || [];
    const collections = results.collections || [];
    const pages = results.pages || [];
    const articles = results.articles || [];

    return [
      ...products.map((item) => ({
        type: 'product',
        title: item.title,
        url: item.url,
        meta: item.vendor || 'Product',
        icon: '□',
      })),

      ...collections.map((item) => ({
        type: 'collection',
        title: item.title,
        url: item.url,
        meta: 'Collection',
        icon: '□',
      })),

      ...pages.map((item) => ({
        type: 'page',
        title: item.title,
        url: item.url,
        meta: 'Page',
        icon: '▤',
      })),

      ...articles.map((item) => ({
        type: 'article',
        title: item.title,
        url: item.url,
        meta: item.blog?.title || 'Article',
        icon: '▤',
      })),
    ].slice(0, 4);
  }

  #renderResult(result) {
    const item = document.createElement('a');

    item.href = result.url;
    item.className = 'search-predictive-item';

    item.innerHTML = `
      <span class="search-predictive-item__icon">
        ${this.#escapeHtml(result.icon || '□')}
      </span>

      <div class="search-predictive-item__content">
        <div class="search-predictive-item__title">
          ${this.#escapeHtml(result.title || '')}
        </div>

        ${
          result.meta
            ? `
              <div class="search-predictive-item__meta">
                ${this.#escapeHtml(result.meta)}
              </div>
            `
            : ''
        }
      </div>
    `;

    this.refs.predictiveList.appendChild(item);
  }

  #renderMoreResults(searchTerm) {
    const moreLink = document.createElement('a');

    const searchUrl = new URL(
      Theme.routes.search_url,
      window.location.origin
    );

    searchUrl.searchParams.set('q', searchTerm);

    moreLink.href = searchUrl.toString();
    moreLink.className = 'search-predictive-more';

    moreLink.textContent = `More Results For "${searchTerm}"`;

    this.refs.predictiveList.appendChild(moreLink);
  }

  #renderEmptyState(searchTerm) {
    const empty = document.createElement('div');

    empty.className = 'search-predictive-empty';

    empty.textContent = `No results found for "${searchTerm}"`;

    this.refs.predictiveList.appendChild(empty);
  }

  #openDropdown() {
    this.refs.predictiveDropdown.hidden = false;
  }

  #closeDropdown() {
    this.refs.predictiveDropdown.hidden = true;
  }

  #handleDocumentClick = (event) => {
    if (!this.contains(event.target)) {
      this.#closeDropdown();
    }
  };

  #escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }
}

if (!customElements.get('search-page-input-component')) {
  customElements.define(
    'search-page-input-component',
    SearchPageInputComponent
  );
}